import type { ApiError } from "@/api/client";
import type { SubscriptionStatus } from "@/api/types";

import { ACTION_LABELS, renewalLabel } from "./gettingStarted";

/**
 * §10's paywall refusal, classified once for every gated route.
 *
 * `require_active_entitlement` derives FOUR reason codes from one seat state —
 * `no_active_seat`, `seat_revoked`, `payment_past_due` and
 * `pending_email_verification` — and every gated route answers with the
 * identical body for the identical account, `POST /subjects/{id}/enroll`
 * included. So the paywall is one recovery surface keyed on the code, not one
 * per route, and the decision lives here rather than at each call site.
 *
 * **§10 orders this as "paywall before enrolment", NOT "402 before 403".**
 * Three of the four codes are 402 and `pending_email_verification` is a 403,
 * so a surface keyed on 402 alone renders a paywall state as a closed door:
 * a learner holding a seat someone else funded, whose mailbox is not yet
 * confirmed (§11.1.10), is told they have no access to the subject when what
 * they need is the verification link. `not_enrolled` is a 403 too but is not
 * a paywall code — it only appears once the account is past the paywall.
 */
export type PaywallRecovery =
  /** No seat, or an expired one: the account can buy its own. */
  | "buy"
  /** Funding withdrawn, refunded, or a card that failed past its grace. */
  | "payer"
  /** A seat is held and funded; the holder's mailbox is not confirmed. */
  | "verify";

/**
 * The reason marker on a `/verify` redirect, and the destination carrying it.
 *
 * `/verify` serves two arrivals that need opposite things said to them. From
 * the purchase path (`email_not_verified`) the account can study and merely
 * cannot buy. From the paywall (`pending_email_verification`) it is the other
 * way round: someone else has already bought and paid for the seat, and
 * studying is what is withheld until the mailbox is confirmed. Copy written
 * for the first arrival is wrong in both halves for the second, so the reason
 * travels with the redirect rather than being guessed at the far end.
 *
 * `pending_email_verification` is §11.1.10's funded-but-unverified holder.
 * The copy promises NO timeframe and scopes "last step" to what the user can
 * actually do.
 */
export const PENDING_SEAT_REASON = "pending_email_verification";
export const VERIFY_PENDING_SEAT = `/verify?reason=${PENDING_SEAT_REASON}`;

/**
 * The one sentence that explains §12.1's 16+ threshold to the person it
 * applies to (rl-757.45).
 *
 * Shared because three surfaces say it — the Today banner, the Account
 * purchase card and `AddSubjectPanel`'s refusal copy — and a child-safety
 * explanation that drifts into three wordings is three chances to get one
 * of them wrong. `age.py`'s docstring makes the same argument about the
 * threshold itself.
 *
 * It names the route an under-16 learner has: §9.5's request to a payer
 * (rl-757.27), worded as `docs/design-onboarding-flows.md`'s 13–15 copy.
 */
export const UNDER_16_PURCHASE_COPY =
  "You need to be 16 to buy a plan for yourself. Plans are bought by someone 16 or " +
  "over — usually a parent or carer. We send them a request and nothing is charged " +
  "until they choose to pay.";

/**
 * §11.1.9 + §11.2.4's refusal: the intended holder already has a seat.
 *
 * **Shared because the NEUTRALITY is the requirement, not because the
 * repetition would be untidy** (rl-757.28). §11.2.4's case is two
 * separated parents funding the same learner, so this sentence must read
 * identically whether that existing seat is one the learner pays for
 * themselves or one a different payer bought — the obvious phrasings
 * ("already funded", "someone else is paying for them") tell one parent
 * what the other has been doing. An obligation like that cannot be
 * enforced by two copies that happen to agree today: each file reads fine
 * on its own, so the drift is invisible until a parent is told something
 * about the other parent. Two consumers — `SeatPurchase`'s buy and
 * `FundedSeats`'s add-another-seat — and any third takes this one too.
 * DO NOT inline it for locality; the §11.2.4 reasoning goes with it
 * when you do.
 *
 * So: no agent, no payer, no date, no "another" and no "already paid" —
 * only the holder's own entitlement state, which the buyer necessarily
 * learns from the refusal itself, and the fact that no money moved. The
 * route out points at the one person entitled to see the billing behind
 * the seat: the holder.
 */
export const REFUSAL_HOLDER_ENTITLED =
  "That account already has a seat, so there is nothing to buy for it and nothing has " +
  "been charged. One account holds one seat. If that looks wrong, the account holder " +
  "can check their own plan from their Account page.";

/**
 * The plan slide-over (§4.2, `rl-757.47`), and the canonical target of every
 * self-purchase this family offers.
 *
 * A panel of its own rather than a paywall branch on `?add=1` (§8 Q3), so
 * the two stay distinct in the URL and in analytics: "opened the plan
 * panel" is countable separately from "opened the subject list".
 */
export const PLAN_PANEL = "/?plan=1";

/**
 * §10's four paywall codes, spelled as `errors.py`'s `ReasonCode` spells
 * them (`api/src/revisionlab_api/errors.py`).
 *
 * That file is the authority, not §5's table: `cancel_at_period_end` is an
 * agreement status rather than a code, and the API sends no "I have
 * verified — continue" action with `pending_email_verification`. §5 is a
 * copy spec, checked against the enum.
 */
export type RecoveryCode =
  | "no_active_seat"
  | "seat_revoked"
  | "payment_past_due"
  | "pending_email_verification";

/**
 * The two §10 codes that block a PURCHASE while leaving the account and the
 * subject alone (§12.1 items 4-5, §9.1).
 *
 * Separate from {@link RecoveryCode} because they are neither paywall
 * refusals nor closed doors: one is a step the reader takes, one is a
 * person they ask, and sharing copy with a genuine access refusal would
 * tell someone whose account is fine that it is not. They are in this
 * family's table all the same, because `AddSubjectPanel` renders them and
 * two tables on one surface is how a code ends up with two voices.
 */
export type PurchaseBlockedCode =
  "email_not_verified" | "age_below_purchase_threshold";

/**
 * Every row this family owns: §10's codes above, plus the one row of §5's
 * recovery table that is not a refusal at all.
 *
 * `cancel_at_period_end` is an agreement and seat status that reaches the
 * client as `status` on `GET /me/subscription` (`subscription.py`
 * `get_my_subscription`). Keeping it in this union but out of
 * {@link RecoveryCode} is what stops it being looked up in `errors.py`'s
 * table, where it has no row and never will.
 */
export type RecoveryKey =
  RecoveryCode | PurchaseBlockedCode | "cancel_at_period_end";

/**
 * The actions §10 offers these codes (`errors.py`'s `Action`), plus the two
 * this family performs with no backend at all.
 *
 * `request_payment` and `notify_payer` are listed HERE AND NEVER RENDERED,
 * deliberately. §10's array order is load-bearing — the first action is the
 * one to lead with — so dropping the unrendered ones from the transcription
 * would hide that `seat_revoked` leads with a conversation while
 * `no_active_seat` leads with a purchase. They are filtered where controls
 * are built instead, leaving {@link RECOVERY_ACTIONS} checkable against
 * `errors.py` line for line. `request_payment` is `rl-757.27` and
 * `notify_payer` is `rl-njh.6`, and this family renders a control for
 * neither.
 */
export type ApiRecoveryAction =
  "purchase_self" | "request_payment" | "notify_payer" | "resend_verification";

export type RecoveryActionId =
  | ApiRecoveryAction
  /** Client-side: the reader's own mail client, so it needs no backend. */
  | "email_payer"
  /** Client-side: the account page, the only place a plan is visible today. */
  | "check_plan";

/**
 * §10's `actions`, transcribed from `errors.py` `REASONS` in its order.
 *
 * Two codes lead with an action this client does not render, and that is the
 * table being right rather than convenient: the server's first choice for a
 * holder whose funding stopped is a conversation with whoever was paying, and
 * the purchase is its fallback. Rendering only the fallback is this client's
 * limitation, not a disagreement about the order.
 */
export const RECOVERY_ACTIONS: Record<
  RecoveryCode | PurchaseBlockedCode,
  readonly ApiRecoveryAction[]
> = {
  no_active_seat: ["purchase_self", "request_payment"],
  seat_revoked: ["request_payment", "purchase_self"],
  payment_past_due: ["notify_payer", "purchase_self"],
  pending_email_verification: ["resend_verification"],
  email_not_verified: ["resend_verification"],
  age_below_purchase_threshold: ["request_payment"],
};

const KEY_BY_CODE: Record<string, RecoveryKey> = {
  no_active_seat: "no_active_seat",
  seat_revoked: "seat_revoked",
  payment_past_due: "payment_past_due",
  pending_email_verification: "pending_email_verification",
  email_not_verified: "email_not_verified",
  age_below_purchase_threshold: "age_below_purchase_threshold",
};

/**
 * Which row of this family's table a refusal renders, or `null` when it has
 * no row and the caller's own generic handling should stand.
 */
export function recoveryKeyFor(error: ApiError): RecoveryKey | null {
  const known = error.code === undefined ? undefined : KEY_BY_CODE[error.code];
  if (known !== undefined) return known;
  // §10 leaves the statuses alone, so a 402 is a paywall refusal whatever
  // code rides on it — including none, and including one added by a server
  // newer than this build. Codes are additive precisely because a client may
  // not switch exhaustively over them, and this is the floor that makes
  // adding one safe: an unrecognised 402 still paywalls, an unrecognised
  // 403 still falls through to the caller's generic refusal.
  return error.status === 402 ? "no_active_seat" : null;
}

/**
 * Partial on purpose: a row exists for every code this family words, and
 * only four of them are PAYWALL refusals. `email_not_verified` has copy
 * here and is still not a paywall — the account can study and merely cannot
 * buy — so it must keep falling through to its caller's own handling.
 */
const PAYWALL_BY_KEY: Partial<Record<RecoveryKey, PaywallRecovery>> = {
  no_active_seat: "buy",
  seat_revoked: "payer",
  payment_past_due: "payer",
  pending_email_verification: "verify",
};

/**
 * The recovery a refusal points at, or `null` when it is not a paywall
 * refusal at all and the caller's own handling should stand.
 *
 * Kept for the surfaces that only need to know WHERE to send someone
 * (`SubjectView` redirects on it) and derived from the same code map as the
 * copy, so a route and the words it arrives with cannot disagree about
 * which refusal happened.
 */
export function paywallRecovery(error: ApiError): PaywallRecovery | null {
  const key = recoveryKeyFor(error);
  return key === null ? null : (PAYWALL_BY_KEY[key] ?? null);
}

/**
 * Which row a `GET /me/subscription` status renders, or `null` while the
 * account is entitled and has nothing to recover from.
 *
 * `pending_email_verification` is deliberately unreachable here. A
 * `pending_verification` seat does not entitle, so `get_entitling_seat`
 * skips it and the endpoint answers `status: null` — exactly what a revoked
 * seat answers. Guessing between them from `isEmailVerified` would tell a
 * holder whose funding was withdrawn that their seat is bought and paid
 * for, which is exactly the lie this table must not tell. That row
 * is rendered where the code actually arrives: on a refusal body.
 */
export function recoveryKeyForStatus(
  status: SubscriptionStatus["status"],
  funded: boolean,
): RecoveryKey | null {
  if (status === "active" || status === "trialing") return null;
  if (status === "past_due") return "payment_past_due";
  if (status === "cancel_at_period_end") return "cancel_at_period_end";
  // `expired`, `canceled`, `paused`, `unpaid`, `incomplete`, `disputed`,
  // `none` and `null` all mean the same thing to the reader — no seat that
  // entitles — and §10 words that as one answer for the same reason
  // (`errors.py`: "a lapsed period and a person who never bought are
  // deliberately one answer"). Who was paying is what changes the words.
  return funded ? "seat_revoked" : "no_active_seat";
}

/** One control the notice renders, resolved from §10's array. */
export interface RecoveryControl {
  id: RecoveryActionId;
  /** §5's label for it — one per action, everywhere. */
  label: string;
  /** A `RouterLink` target, or a `mailto:` when {@link external}. */
  target: string;
  /** `mailto:` is a plain anchor rather than a route. */
  external: boolean;
}

/**
 * §4.5's 32px tile, named rather than imported so this file stays a pure
 * module the copy can be tested through without mounting anything.
 */
export type RecoveryIcon =
  "plan" | "payer" | "paused" | "payment" | "mail" | "calendar";

export interface RecoveryInput {
  key: RecoveryKey;
  /** Non-null => somebody else funds this account's seat (§9.6). */
  funderFirstName: string | null;
  /**
   * Withheld from a claimant whose address did not match (`GET /me/claim`),
   * and the same withholding reaches here, so a surface offering to mail
   * the payer must tolerate knowing their name and not their address.
   */
  funderEmail: string | null;
  /**
   * §12.1's 16+ answer (`rl-757.45`). `null` means NOT YET KNOWN and is
   * permissive: hiding the only route forward from a 16-year-old because one
   * request was slow is worse than briefly offering a control that answers
   * `age_below_purchase_threshold`, for which copy exists.
   */
  canSelfPurchase: boolean | null;
  /**
   * Whether the READER is the one whose payment failed — §4.5's warm
   * variant, and the only thing it keys on.
   *
   * A separate fact from "no funder" because absence of a funder means two
   * different things on the two surfaces that render this: on Today
   * `GET /me/subscription` has answered and a null funder really does mean
   * self-funded, while on a refusal body nobody asked. Defaulting the
   * unasked case to `false` keeps the warm tint off a reader who may owe
   * nothing, which is the asymmetry §9.6 is about.
   */
  readerOwesPayment: boolean;
  /** Whether this account has ever held a plan of its own. */
  hadPlan: boolean;
  /** ISO `current_period_end`, for the row that names a date. */
  endsAt: string | null;
}

export interface RecoveryView {
  key: RecoveryKey;
  icon: RecoveryIcon;
  /**
   * `warn` ONLY where the reader personally owes the money. On anyone else
   * the warm tint reads as an accusation about a card they do not own, and
   * on a 13-15 holder it decorates a purchase §12 forbids them
   * (`rl-757.51`).
   */
  tone: "neutral" | "warn";
  title: string;
  body: string;
  controls: RecoveryControl[];
}

/**
 * §5 gives the self-purchase two labels, and which applies is a fact about
 * the reader rather than the action: "Start your plan" to someone who never
 * had one or whose own plan ended, "Start a plan for yourself" to a holder
 * whose seat somebody else was paying for, for whom "your plan" names the
 * thing that just stopped being theirs.
 */
const SELF_PURCHASE_INSTEAD = "Start a plan for yourself";

/**
 * The account page, which is where an existing plan is visible.
 *
 * NOT a billing portal (`rl-757.28`): this points at the page rather than
 * promising a card form. It is
 * offered only to a reader whose own payment failed — see
 * {@link RecoveryInput.readerOwesPayment}.
 */
const CHECK_PLAN: RecoveryControl = {
  id: "check_plan",
  label: "Check the plan",
  target: "/account",
  external: false,
};

function apiControl(
  id: ApiRecoveryAction,
  key: RecoveryKey,
  input: RecoveryInput,
): RecoveryControl | null {
  switch (id) {
    case "request_payment":
    case "notify_payer":
      // `rl-757.27` and `rl-njh.6`. §10's own first choice for two of these
      // codes renders as words and nothing clickable.
      return null;
    case "purchase_self":
      // §12.1, enforced by the refusal rather than by this line: a stale
      // `true` earns a clean `age_below_purchase_threshold` the surfaces
      // already word, a stale `false` merely loses an affordance.
      if (input.canSelfPurchase === false) return null;
      // §9.6's asymmetry on one side and `already_subscribed` on the
      // other. A holder whose payer's payment failed must not be told to
      // buy: it is not their card, and for a 13-15 holder it is not a
      // thing they may do at all. A reader whose own payment failed still
      // holds the subscription, so `POST /subscribe` refuses them
      // `already_subscribed` (`subscription.py`
      // `create_subscription_checkout`). Neither has a purchase to make, which
      // is why §5's "Pay for it yourself (16+ only)" on this row is corrected
      // in the design document.
      if (key === "payment_past_due") return null;
      return {
        id,
        label:
          key === "seat_revoked" ? SELF_PURCHASE_INSTEAD : ACTION_LABELS.plan,
        target: PLAN_PANEL,
        external: false,
      };
    case "resend_verification":
      // The reason travels with the redirect, and WHICH reason is the whole
      // point: `/verify` serves two arrivals that need opposite things
      // said to them. From the paywall someone else has already paid and
      // studying is what waits on the mailbox; from the purchase path the
      // account can study and merely cannot buy. Copy written for one is
      // wrong in both halves for the other, so only the paywall code
      // carries the marker. §5's other verify label, "I have verified —
      // continue", is a session refresh that lives on the page this leads
      // to rather than a second copy of it here.
      return {
        id,
        label: ACTION_LABELS.resend,
        target:
          key === "pending_email_verification"
            ? VERIFY_PENDING_SEAT
            : "/verify",
        external: false,
      };
  }
}

function emailPayer(input: RecoveryInput): RecoveryControl | null {
  if (input.funderFirstName === null || input.funderEmail === null) return null;
  return {
    id: "email_payer",
    label: `Email ${input.funderFirstName}`,
    target: `mailto:${input.funderEmail}`,
    external: true,
  };
}

/**
 * §5's recovery copy for one reader, with §10's action order preserved.
 *
 * One function rather than one per surface: a Today banner keyed on
 * subscription `status` and an `AddSubjectPanel` keyed on
 * {@link PaywallRecovery} would give a single refusal two voices depending on
 * which surface the reader reached first. §5 mandates one label per action
 * everywhere, which only holds while one thing owns the words.
 */
export function recoveryNotice(
  input: RecoveryInput,
  now: Date = new Date(),
): RecoveryView {
  const funder = input.funderFirstName;
  const controls: RecoveryControl[] = [];
  if (input.key !== "cancel_at_period_end") {
    for (const id of RECOVERY_ACTIONS[input.key]) {
      const resolved = apiControl(id, input.key, input);
      if (resolved !== null) controls.push(resolved);
    }
  }

  switch (input.key) {
    case "no_active_seat":
    case "seat_revoked": {
      // §4.5 makes the 13-15 variance a FILTER ON THE ACTIONS ARRAY, not a
      // different notice, and the distinction is load-bearing: the age fact
      // explains why there is no button, it does not replace the question of
      // WHO MUST ACT. Answering it first would tell a 13-15 holder whose
      // funding has stopped "you need to be 16 to buy a plan" and never name
      // the person who stopped paying -- undoing rl-757.51's §9.6 asymmetry
      // for exactly the reader with no alternative, while a 16+ holder in the
      // same position is told properly. Each half is individually right, so
      // only rendering the branches shows it.
      const tooYoung = input.canSelfPurchase === false;
      if (tooYoung && funder === null) {
        // Nobody to name AND no purchase to make, so the age sentence is
        // the whole answer. The same wording the Account purchase card and
        // `AddSubjectPanel` use, rather than a fourth for one child-safety
        // fact. `controls` is empty by construction: the purchase is
        // filtered and §10's other action is not rendered.
        return {
          key: input.key,
          icon: "payer",
          tone: "neutral",
          title: "Someone needs to buy your seat",
          body: `${UNDER_16_PURCHASE_COPY} Your progress is saved either way.`,
          controls,
        };
      }
      if (input.key === "seat_revoked") {
        return {
          key: input.key,
          icon: "payer",
          tone: "neutral",
          title: "Your seat is no longer being funded",
          body:
            funder === null
              ? "Your work is saved and stays yours. You can start a plan for yourself, or " +
                "someone with a plan can add a seat for your address."
              : `${funder} is no longer paying for your seat. Your work is saved and stays ` +
                "yours — " +
                (tooYoung
                  ? // Under 16 there is no "start a plan for yourself" to
                    // offer, so the sentence must not name it: §12.1 forbids
                    // the purchase and no control renders for it.
                    `ask ${funder} to start it again, or anyone with a plan can add a seat ` +
                    "for your address."
                  : "you can start a plan for yourself, or someone with a plan can add " +
                    "a seat for your address."),
          controls,
        };
      }
      return {
        key: input.key,
        icon: "plan",
        tone: "neutral",
        // §10 answers "never had a seat" and "no longer has one" with one
        // code on purpose, but the client knows which reader it has, and
        // telling someone who never subscribed that their plan ended is
        // a smaller version of the same lie.
        title: input.hadPlan
          ? "Your plan has ended"
          : "You do not have a plan yet",
        body: input.hadPlan
          ? "Your progress is saved. Start it again to pick up sessions where you left off."
          : "You can still see what you have added. Adding more subjects, sessions and " +
            "marking all need an active plan — one plan covers every subject and board.",
        controls,
      };
    }
    case "payment_past_due": {
      if (input.readerOwesPayment) {
        // The one warm row. No timeframe: "within 7 days" is Stripe's
        // retry schedule, which this client does not read and cannot
        // promise, and §5's "Update card" (`rl-757.28`'s portal) is not
        // offered here.
        return {
          key: input.key,
          icon: "payment",
          tone: "warn",
          title: "Your payment did not go through",
          body:
            "Your access continues for now and your progress is saved. The plan stops " +
            "if the payment is not settled.",
          controls: [...controls, CHECK_PLAN],
        };
      }
      const mail = emailPayer(input);
      if (mail !== null) controls.push(mail);
      return {
        key: input.key,
        icon: "paused",
        tone: "neutral",
        title: "Your access is paused",
        // Named, because "contact the person funding your account" is not
        // actionable to a 14-year-old and naming them is the whole fix.
        // "Payment", never "card": the holder never sees a card error for
        // a card they do not own (§9.6), which is the argument
        // `errors.py` `REASONS` makes about this exact code's message.
        body:
          funder === null
            ? "The payment for your seat needs updating before sessions start again. " +
              "Your progress is saved, and whoever pays for it can sort this out from " +
              "their own account."
            : `${funder} pays for your seat, and the payment needs updating before ` +
              "sessions start again. Your progress is saved. Let them know — they can " +
              "sort it out from their own account.",
        controls,
      };
    }
    case "pending_email_verification":
      return {
        key: input.key,
        icon: "mail",
        tone: "neutral",
        title: "Confirm your email to open your seat",
        // NO TIMEFRAME, deliberately: this scopes the promise to the step
        // the reader can actually take.
        body:
          "Your seat is bought and paid for. Confirm your email address, then return " +
          "here — we will open your access on that check.",
        controls,
      };
    case "email_not_verified":
      return {
        key: input.key,
        icon: "mail",
        tone: "neutral",
        title: "Confirm your email address",
        // §9.1: the account and the subject are both fine and only the
        // purchase is blocked, so this shares copy with neither the paywall
        // nor a closed door. It is a step the reader takes.
        body: "Verify your email address first — check your inbox for the link we sent.",
        controls,
      };
    case "age_below_purchase_threshold":
      return {
        key: input.key,
        icon: "payer",
        tone: "neutral",
        // Deliberately the same title and the same sentence the under-16
        // branch above uses. One fact, one wording: a child-safety
        // explanation that drifts into a second voice because a different
        // surface rendered it is two chances to get one of them wrong.
        title: "Someone needs to buy your seat",
        body: UNDER_16_PURCHASE_COPY,
        controls,
      };
    case "cancel_at_period_end": {
      const ends = renewalLabel(input.endsAt, now);
      return {
        key: input.key,
        icon: "calendar",
        tone: "neutral",
        title:
          ends === null ? "Your plan is ending" : `Your plan ends on ${ends}`,
        // §5's "Keep my plan" is `rl-757.28`'s portal, so no control is
        // offered.
        body:
          "You can keep studying until then. Your progress is saved and stays yours, " +
          "and nothing more will be charged.",
        controls,
      };
    }
  }
}
